import React, { useCallback, useEffect, useState } from "react";
import { formatDateTime } from "../utils/formatDate";
import { Modal, Nav, Form, Button, Spinner } from "react-bootstrap";
import {
  FaShieldAlt,
  FaRobot,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserShield,
  FaMagic,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const brandBlue = "#263F86";
const borderColor = "#EEF0F4";
const successColor = "#047857";
const dangerColor = "#B91C1C";
const mutedColor = "#64748B";

const MAX_TOPIC_LENGTH = 200;
const MAX_SOURCE_LENGTH = 6000;

const noticeSections = [
  {
    title: "What is sent to OpenAI",
    items: [
      "Only the topic and reference text typed in by an administrator, after personal data has been removed.",
    ],
  },
  {
    title: "What is never sent",
    items: [
      "Learner accounts, names, NRIC/FIN, emails, phone numbers.",
      "Learner answers, scores, results and audit logs.",
    ],
  },
  {
    title: "Safeguards",
    items: [
      "NRIC/FIN, emails, phone numbers and registered names are replaced with placeholders on the server before sending.",
      "Every request is recorded in Audit Logs (without its content). Administrators only, with a request limit.",
      "AI output is a draft. Nothing is saved until an administrator reviews it.",
    ],
  },
];

const cardStyle = {
  background: "#F8FAFC",
  border: `1px solid ${borderColor}`,
  borderRadius: "16px",
  padding: "16px 18px",
};

const labelStyle = {
  fontWeight: 700,
  fontSize: "13px",
  color: "#1F2937",
};

const inputStyle = {
  borderRadius: "10px",
  border: `1px solid ${borderColor}`,
  fontSize: "14px",
  padding: "10px 12px",
};

const formatDate = (iso) => formatDateTime(iso, "");

const describeRedaction = (redaction) => {
  if (!redaction || redaction.total === 0) return "";

  const labels = { NRIC: "NRIC/FIN", EMAIL: "email", PHONE: "phone", NAME: "name" };

  return Object.entries(redaction.counts)
    .filter(([, value]) => value > 0)
    .map(([type, value]) => `${value} ${labels[type]}`)
    .join(", ");
};

function AiQuestionGenerator({ show, onHide, user }) {
  const [tab, setTab] = useState("protection");
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [consent, setConsent] = useState(false);

  const [assessments, setAssessments] = useState([]);
  const [tags, setTags] = useState([]);
  const [form, setForm] = useState({
    assessmentId: "",
    tagsId: "",
    topic: "",
    source: "",
    count: 5,
  });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [redaction, setRedaction] = useState(null);

  const api = process.env.REACT_APP_API;

  const loadSettings = useCallback(async () => {
    if (!user?.user_id) return;

    try {
      setSettingsLoading(true);
      const response = await axios.get(`${api}/getPdpaSettings`, {
        params: { user_id: user.user_id },
      });

      if (response.data.success) {
        setSettings(response.data.data);
        setTab(response.data.data.enabled ? "generate" : "protection");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load data protection settings.",
        { position: "top-right", autoClose: 3000 },
      );
    } finally {
      setSettingsLoading(false);
    }
  }, [api, user?.user_id]);

  useEffect(() => {
    if (!show) return;

    setConsent(false);
    setDrafts([]);
    setRedaction(null);
    loadSettings();

    axios
      .get(`${api}/getAllAssessment`)
      .then((response) => {
        if (response.data.success) setAssessments(response.data.data || []);
      })
      .catch(() => setAssessments([]));

    axios
      .get(`${api}/getTags`)
      .then((response) => {
        if (response.data.success) setTags(response.data.data || []);
      })
      .catch(() => setTags([]));
  }, [show, api, loadSettings]);

  const updateEnabled = async (enabled) => {
    try {
      setSettingsSaving(true);
      const response = await axios.post(`${api}/savePdpaSettings`, {
        user_id: user.user_id,
        enabled,
        consent: enabled ? consent : undefined,
      });

      toast.success(response.data.message, {
        position: "top-right",
        autoClose: 2500,
      });

      setConsent(false);
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save settings.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    if (!form.topic.trim() && !form.source.trim()) {
      toast.error("Please enter a topic or some reference text.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setGenerating(true);
      setDrafts([]);
      setRedaction(null);

      const response = await axios.post(`${api}/generateAiQuestions`, {
        user_id: user.user_id,
        topic: form.topic,
        source_text: form.source,
        count: form.count,
      });

      setDrafts(
        (response.data.questions || []).map((question) => ({
          ...question,
          include: true,
        })),
      );
      setRedaction(response.data.redaction);

      toast.info(response.data.message, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate questions.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const toggleDraft = (index) => {
    setDrafts((prev) =>
      prev.map((draft, i) =>
        i === index ? { ...draft, include: !draft.include } : draft,
      ),
    );
  };

  const selectedDrafts = drafts.filter((draft) => draft.include);

  const handleSave = async () => {
    if (!form.assessmentId || !form.tagsId) {
      toast.error("Please choose an assessment and a tag.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setSaving(true);

      const response = await axios.post(`${api}/saveAiQuestions`, {
        user_id: user.user_id,
        assessment_id: form.assessmentId,
        tags_id: form.tagsId,
        questions: selectedDrafts.map(({ include, ...question }) => question),
      });

      toast.success(response.data.message, {
        position: "top-right",
        autoClose: 3000,
      });

      setDrafts([]);
      setRedaction(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save questions.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderProtection = () => (
    <>
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
        style={{
          ...cardStyle,
          background: settings?.enabled ? "#ECFDF5" : "#F8FAFC",
          borderColor: settings?.enabled ? "#A7F3D0" : borderColor,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          {settings?.enabled ? (
            <FaCheckCircle color={successColor} />
          ) : (
            <FaUserShield color={brandBlue} />
          )}
          <div>
            <div style={{ fontWeight: 800, color: "#1F2937" }}>
              OpenAI is {settings?.enabled ? "ON" : "OFF"}
            </div>
            {settings?.enabled && settings.consentAcceptedBy && (
              <div style={{ fontSize: "12px", color: mutedColor }}>
                Consent recorded by {settings.consentAcceptedBy} on{" "}
                {formatDate(settings.consentAcceptedAt)}
              </div>
            )}
          </div>
        </div>

        {settings?.enabled && (
          <Button
            variant="light"
            disabled={settingsSaving}
            onClick={() => updateEnabled(false)}
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "13px",
            }}
          >
            Turn off
          </Button>
        )}
      </div>

      {settings && !settings.apiKeyConfigured && (
        <div
          className="d-flex gap-2 mb-3"
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: dangerColor,
            borderRadius: "12px",
            padding: "12px 14px",
            fontSize: "13px",
          }}
        >
          <FaExclamationTriangle className="mt-1 flex-shrink-0" />
          <span>
            No OpenAI API key is set on the server. Add OPENAI_API_KEY to the
            server .env file and restart the server.
          </span>
        </div>
      )}

      <div className="row g-3 mb-3">
        {noticeSections.map((section) => (
          <div className="col-12 col-md-4" key={section.title}>
            <div style={{ ...cardStyle, height: "100%" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "13px",
                  color: brandBlue,
                  marginBottom: "8px",
                }}
              >
                {section.title}
              </div>
              <ul
                className="mb-0 ps-3"
                style={{ fontSize: "12.5px", color: "#475569" }}
              >
                {section.items.map((item) => (
                  <li key={item} className="mb-1">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: "12.5px", color: mutedColor }}>
        OpenAI processes data outside Singapore, so this counts as an overseas
        transfer under the PDPA. Automatic removal is a safety net, not a
        guarantee (for example addresses or nicknames may not be detected), so
        please do not paste personal data. Your Data Protection Officer should
        approve this use and confirm OpenAI&apos;s data processing terms.
      </p>

      {settings && !settings.enabled && (
        <div style={cardStyle}>
          <Form.Check
            type="checkbox"
            id="pdpa-consent"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
            label="I have read this notice and approve sending de-identified question content to OpenAI on behalf of the school."
            style={{ fontSize: "13px", color: "#1F2937" }}
          />

          <Button
            className="mt-3"
            disabled={!consent || settingsSaving}
            onClick={() => updateEnabled(true)}
            style={{
              background: brandBlue,
              border: "none",
              borderRadius: "10px",
              fontWeight: 700,
              padding: "9px 18px",
            }}
          >
            {settingsSaving ? "Saving..." : "Enable OpenAI"}
          </Button>
        </div>
      )}
    </>
  );

  const renderGenerate = () => {
    if (!settings?.enabled) {
      return (
        <div className="text-center py-4" style={{ color: mutedColor }}>
          <FaShieldAlt size={26} color={brandBlue} className="mb-2" />
          <p className="mb-3">
            OpenAI is turned off. Review the data protection notice and enable
            it first.
          </p>
          <Button
            variant="light"
            onClick={() => setTab("protection")}
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: "10px",
              fontWeight: 700,
            }}
          >
            Open Data Protection
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <Form.Label style={labelStyle}>Assessment</Form.Label>
            <Form.Select
              name="assessmentId"
              value={form.assessmentId}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select assessment</option>
              {assessments.map((assessment) => (
                <option
                  key={assessment.assessment_id}
                  value={assessment.assessment_id}
                >
                  {assessment.assessment_name}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-12 col-md-6">
            <Form.Label style={labelStyle}>Tag</Form.Label>
            <Form.Select
              name="tagsId"
              value={form.tagsId}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select tag</option>
              {tags.map((tag) => (
                <option key={tag.tags_id} value={tag.tags_id}>
                  {tag.tags_name}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-12 col-md-9">
            <Form.Label style={labelStyle}>Topic</Form.Label>
            <Form.Control
              name="topic"
              value={form.topic}
              maxLength={MAX_TOPIC_LENGTH}
              onChange={handleChange}
              placeholder="e.g. Cell structure and functions"
              style={inputStyle}
            />
          </div>

          <div className="col-12 col-md-3">
            <Form.Label style={labelStyle}>Questions</Form.Label>
            <Form.Select
              name="count"
              value={form.count}
              onChange={handleChange}
              style={inputStyle}
            >
              {[3, 5, 8, 10].map((number) => (
                <option key={number} value={number}>
                  {number}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-12">
            <Form.Label style={labelStyle}>
              Reference text (optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="source"
              value={form.source}
              maxLength={MAX_SOURCE_LENGTH}
              onChange={handleChange}
              placeholder="Paste teaching material here. Do not include personal data."
              style={inputStyle}
            />
            <div className="d-flex justify-content-between mt-1">
              <small style={{ color: mutedColor }}>
                Personal data found is removed before sending.
              </small>
              <small style={{ color: mutedColor }}>
                {form.source.length}/{MAX_SOURCE_LENGTH}
              </small>
            </div>
          </div>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            background: brandBlue,
            border: "none",
            borderRadius: "10px",
            fontWeight: 700,
            padding: "9px 18px",
          }}
        >
          {generating ? (
            <>
              <Spinner size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            <>
              <FaMagic className="me-2" size={13} />
              Generate questions
            </>
          )}
        </Button>

        {redaction && (
          <div
            className="d-flex gap-2 mt-3"
            style={{
              background: redaction.total > 0 ? "#FFFBEB" : "#ECFDF5",
              border: `1px solid ${redaction.total > 0 ? "#FDE68A" : "#A7F3D0"}`,
              borderRadius: "12px",
              padding: "10px 14px",
              fontSize: "13px",
              color: "#1F2937",
            }}
          >
            <FaShieldAlt className="mt-1 flex-shrink-0" color={brandBlue} />
            <span>
              {redaction.total > 0
                ? `Removed before sending: ${describeRedaction(redaction)}.`
                : "No personal data was detected in your input."}
            </span>
          </div>
        )}

        {drafts.length > 0 && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold mb-0" style={{ color: "#1F2937" }}>
                Review drafts
              </h6>
              <small style={{ color: mutedColor }}>
                {selectedDrafts.length} of {drafts.length} selected
              </small>
            </div>

            {drafts.map((draft, index) => (
              <div
                key={index}
                className="mb-2"
                style={{
                  ...cardStyle,
                  background: draft.include ? "#fff" : "#F8FAFC",
                  opacity: draft.include ? 1 : 0.6,
                }}
              >
                <Form.Check
                  type="checkbox"
                  id={`ai-draft-${index}`}
                  checked={draft.include}
                  onChange={() => toggleDraft(index)}
                  label={`${index + 1}. ${draft.question_text}`}
                  style={{ fontWeight: 700, fontSize: "14px" }}
                />

                <ul className="list-unstyled mb-0 mt-2 ms-4">
                  {draft.options.map((option, optionIndex) => (
                    <li
                      key={optionIndex}
                      style={{
                        fontSize: "13px",
                        color: option.is_correct ? successColor : "#475569",
                        fontWeight: option.is_correct ? 700 : 500,
                      }}
                    >
                      {option.is_correct ? "✓ " : "• "}
                      {option.answer_text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <Button
              className="mt-2"
              onClick={handleSave}
              disabled={saving || selectedDrafts.length === 0}
              style={{
                background: brandBlue,
                border: "none",
                borderRadius: "10px",
                fontWeight: 700,
                padding: "9px 18px",
              }}
            >
              {saving
                ? "Saving..."
                : `Add ${selectedDrafts.length} question(s) to assessment`}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      style={{ fontFamily: "Manrope-Regular" }}
    >
      <Modal.Header
        closeButton
        style={{ borderBottom: `1px solid ${borderColor}`, padding: "20px 24px" }}
      >
        <Modal.Title
          style={{ color: brandBlue, fontWeight: 800, fontSize: "20px" }}
        >
          <FaRobot className="me-2" size={18} />
          AI & Data Protection
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        <Nav
          variant="pills"
          activeKey={tab}
          onSelect={(key) => setTab(key)}
          className="mb-4 gap-2"
        >
          <Nav.Item>
            <Nav.Link
              eventKey="protection"
              style={{
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "14px",
                background: tab === "protection" ? brandBlue : "#F8FAFC",
                color: tab === "protection" ? "#fff" : brandBlue,
              }}
            >
              Data Protection (PDPA)
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="generate"
              style={{
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "14px",
                background: tab === "generate" ? brandBlue : "#F8FAFC",
                color: tab === "generate" ? "#fff" : brandBlue,
              }}
            >
              Generate Questions
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {settingsLoading || !settings ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: brandBlue }} />
          </div>
        ) : tab === "protection" ? (
          renderProtection()
        ) : (
          renderGenerate()
        )}
      </Modal.Body>
    </Modal>
  );
}

export default AiQuestionGenerator;
